import React, { useEffect } from 'react';
import { Typography, Box, Alert } from '@mui/material';
import { UpdateAuthDataValues } from 'Types';
import { CustomTextField, CustomButton } from 'Components/StyledComponents';
import { FormikProps } from 'formik';
import { isPhoneNumberWithCountryCode, validateEmailOrPhone } from './SignUpForm';
import * as yup from 'yup';
import CountryCodeSelect from './CountryCodeSelect';

export type UpdateProfileFormProps = {
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  successMessage?: string;
  disabled?: { [key: string]: boolean | undefined };
} & FormikProps<UpdateAuthDataValues>;

// eslint-disable-next-line react-refresh/only-export-components -- shared schema
export const updateProfileFormSchema = yup.object().shape({
  name: yup.string(),
  emailOrPhone: yup
    .string()
    .test('validateEmailorPhone', 'Must be an email or phone number', (value) => {
      if (!value) return true;
      return validateEmailOrPhone(value) || isPhoneNumberWithCountryCode(value);
    }),
  countryAcronym: yup.string(),
});

const UpdateProfileForm: React.FC<UpdateProfileFormProps> = (props: UpdateProfileFormProps) => {
  const { handleChange, values, errors, successMessage, disabled, dirty } = props;

  const [isPhoneNumber, setIsPhoneNumber] = React.useState(false);

  useEffect(() => {
    if (isPhoneNumberWithCountryCode(values.emailOrPhone)) {
      setIsPhoneNumber(true);
    } else {
      setIsPhoneNumber(false);
    }
  }, [values.emailOrPhone]);
  return (
    <div>
      <CustomTextField
        sx={{ marginBottom: '20px' }}
        id="name"
        name="name"
        type="text"
        onChange={handleChange}
        label="Full Name"
        value={values.name || ''}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        disabled={disabled?.name}
      />

      <Box
        sx={{
          minWidth: 310,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {isPhoneNumber && (
          <CountryCodeSelect handleChange={handleChange} countryAcronym={values.countryAcronym} />
        )}

        <CustomTextField
          sx={{ marginBottom: '20px' }}
          onChange={handleChange}
          id="emailOrPhone"
          name="emailOrPhone"
          type="emailOrPhone"
          label="Email or Phone Number"
          value={values.emailOrPhone || ''}
          error={!!errors.emailOrPhone}
          helperText={errors.emailOrPhone}
          fullWidth
          disabled={disabled?.emailOrPhone}
        />
      </Box>

      <Box>
        <Typography color="error" variant="h6">
          {errors.unknown}
        </Typography>
        {successMessage && (
          <Alert
            severity="success"
            sx={{
              marginBottom: '16px',
              color: '#D4F8DF',
              backgroundColor: 'rgba(64, 225, 109, 0.16)',
              '& .MuiAlert-icon': {
                color: '#40E16D',
              },
            }}
          >
            {successMessage}
          </Alert>
        )}
      </Box>

      <CustomButton
        variant="contained"
        type="submit"
        sx={{
          marginBottom: '20px',
          '&:hover': {
            background: 'rgb(202,124,98,1.0)',
            cursor: 'pointer',
            transition: '0.3s',
          },
          '&.Mui-disabled': {
            backgroundColor: '#767C8C',
          },
        }}
        disabled={!dirty}
      >
        SAVE
      </CustomButton>
    </div>
  );
};

export default UpdateProfileForm;
